import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import {
	classesOfGender,
	presetClasses,
	WEIGHT_CLASS_PRESETS,
} from "../../lib/performanceModelUtils";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

const GENDERS = [
	{ value: "male", labelKey: "pmMale" },
	{ value: "female", labelKey: "pmFemale" },
];

const toDraft = (classes) => {
	const draft = {};
	GENDERS.forEach(({ value }) => {
		const own = classesOfGender(classes, value);
		draft[value] = {
			caps: own
				.filter((item) => item.max_kg !== null && item.max_kg !== undefined)
				.map((item) => Number(item.max_kg)),
			open: own.some((item) => item.max_kg === null || item.max_kg === undefined),
		};
	});
	return draft;
};

const fromDraft = (draft) =>
	GENDERS.flatMap(({ value }) => [
		...(draft[value]?.caps || []).map((max) => ({ gender: value, max_kg: max })),
		...(draft[value]?.open ? [{ gender: value, max_kg: null }] : []),
	]);

const formatCap = (value) => String(value).replace(".", ",");

/**
 * Le categorie di peso di uno sport (D6b di docs/sport-config).
 *
 * Sono dello sport, non del modello: valgono per tutti i suoi modelli, ed e'
 * cosi' che la categoria di un atleta resta la stessa chiunque lo valuti. Le
 * modifica chi governa lo sport: l'admin, l'admin di quella disciplina, o il
 * coach che ha creato lo sport. I preset IWF e IPF sono un punto di partenza.
 *
 * Props: open, onOpenChange, sportId, sportLabel, classes, onSaved(classes).
 */
export function WeightClassesDialog({
	open,
	onOpenChange,
	sportId,
	sportLabel,
	classes = [],
	onSaved,
}) {
	const { t } = useLanguage();
	const [draft, setDraft] = useState(() => toDraft(classes));
	const [inputs, setInputs] = useState({ male: "", female: "" });
	const [saving, setSaving] = useState(false);
	// La bozza riparte dalle categorie salvate a ogni apertura, non a ogni
	// render del genitore: un array nuovo a ogni render la azzererebbe mentre
	// si scrive.
	const classesRef = useRef(classes);
	classesRef.current = classes;

	useEffect(() => {
		if (open) {
			setDraft(toDraft(classesRef.current));
			setInputs({ male: "", female: "" });
		}
	}, [open]);

	const addCap = (gender) => {
		const value = Number(String(inputs[gender] || "").replace(",", "."));
		if (!Number.isFinite(value) || value <= 0 || value > 400) {
			toast.error(t("wcInvalidCap"));
			return;
		}
		setDraft((prev) => {
			const caps = Array.from(new Set([...(prev[gender]?.caps || []), value])).sort(
				(a, b) => a - b,
			);
			return { ...prev, [gender]: { ...prev[gender], caps } };
		});
		setInputs((prev) => ({ ...prev, [gender]: "" }));
	};

	const removeCap = (gender, value) =>
		setDraft((prev) => ({
			...prev,
			[gender]: {
				...prev[gender],
				caps: (prev[gender]?.caps || []).filter((cap) => cap !== value),
			},
		}));

	const toggleOpen = (gender) =>
		setDraft((prev) => ({
			...prev,
			[gender]: { ...prev[gender], open: !prev[gender]?.open },
		}));

	const applyPreset = (preset) => setDraft(toDraft(presetClasses(preset)));

	const save = async () => {
		setSaving(true);
		try {
			const result = await fetchAPI(
				`/sports/${encodeURIComponent(sportId)}/weight-classes`,
				{
					method: "PUT",
					body: JSON.stringify({ weight_classes: fromDraft(draft) }),
				},
			);
			toast.success(t("wcSaved"));
			onSaved?.(result?.weight_classes || []);
			onOpenChange(false);
		} catch (error) {
			const detail = error?.detail;
			toast.error(
				`${t("error")}: ${typeof detail === "string" ? detail : error?.message || ""}`,
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
			<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] overflow-y-auto">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle>
						{t("wcTitle").replace("{sport}", sportLabel || "")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 text-sm">
					<p className="text-muted-foreground">{t("wcHint")}</p>
					<div className="flex flex-wrap items-center gap-2">
						<span className="text-xs text-muted-foreground">{t("wcPresets")}</span>
						{WEIGHT_CLASS_PRESETS.map((preset) => (
							<Button
								key={preset.id}
								type="button"
								size="sm"
								variant="outline"
								className="h-7 rounded-sm text-xs"
								disabled={saving}
								onClick={() => applyPreset(preset)}
							>
								{t(preset.labelKey)}
							</Button>
						))}
					</div>
					{GENDERS.map(({ value: gender, labelKey }) => {
						const caps = draft[gender]?.caps || [];
						const top = caps.length > 0 ? caps[caps.length - 1] : null;
						return (
							<div
								key={gender}
								className="space-y-2 rounded-sm border border-border p-3"
								data-testid={`wc-${gender}`}
							>
								<p className="font-medium">{t(labelKey)}</p>
								<div className="flex flex-wrap gap-1.5">
									{caps.length === 0 && !draft[gender]?.open && (
										<span className="text-xs text-muted-foreground">{t("wcNone")}</span>
									)}
									{caps.map((cap) => (
										<span
											key={cap}
											className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-xs"
										>
											-{formatCap(cap)} kg
											<button
												type="button"
												aria-label={t("wcRemove").replace("{cap}", formatCap(cap))}
												className="text-muted-foreground hover:text-red-400"
												disabled={saving}
												onClick={() => removeCap(gender, cap)}
											>
												<X className="w-3 h-3" />
											</button>
										</span>
									))}
									{draft[gender]?.open && (
										<span className="inline-flex items-center rounded-sm bg-secondary px-2 py-1 text-xs">
											{top !== null ? `+${formatCap(top)} kg` : "Open"}
										</span>
									)}
								</div>
								<div className="flex flex-wrap items-center gap-2">
									<Input
										type="number"
										min="1"
										step="0.5"
										value={inputs[gender]}
										disabled={saving}
										aria-label={t("wcCapLabel")}
										placeholder={t("wcCapPlaceholder")}
										onChange={(event) => {
											const text = event.target.value;
											setInputs((prev) => ({ ...prev, [gender]: text }));
										}}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												addCap(gender);
											}
										}}
										className="h-8 w-28 bg-secondary border-transparent rounded-sm"
									/>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										className="h-8 rounded-sm"
										disabled={saving || !String(inputs[gender] || "").trim()}
										onClick={() => addCap(gender)}
									>
										<Plus className="w-3.5 h-3.5 mr-1" />
										{t("wcAdd")}
									</Button>
									<label className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
										<input
											type="checkbox"
											checked={!!draft[gender]?.open}
											disabled={saving}
											onChange={() => toggleOpen(gender)}
										/>
										{t("wcOpenClass")}
									</label>
								</div>
							</div>
						);
					})}
					<p className="text-xs text-muted-foreground">{t("wcKeepsTables")}</p>
					<div className="flex justify-end gap-2">
						<Button
							variant="ghost"
							className="rounded-sm"
							disabled={saving}
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							disabled={saving}
							onClick={save}
						>
							{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
							{t("save")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
